import React from "react";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { useTranslation } from "react-i18next";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "react-beautiful-dnd";
import { OfferItem } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ResetButton,
    ImageHeader,
    Select,
    AddButton,
    LanguageTabs,
    LanguageTab,
    TableWrapper,
    Table,
    Th,
    Td,
    DragHandle,
    OrderInput,
    OfferImageCell,
    TableInput,
    ActionsCell,
    SmallToggle,
    DeleteButton,
} from "./styled";
import { ICON_SIZE_SMALL } from "../shared/constants";

type OffersTableProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedLanguage: string;
    onLanguageChange: (lang: string) => void;
    displayType: string;
    onDisplayTypeChange: (value: string) => void;
    currentOffers: OfferItem[];
    uploadedImages: Record<number, string>;
    onDragEnd: (result: DropResult) => void;
    onOrderChange: (offerId: number, newOrder: string) => void;
    onImageClick: (offerId: number) => void;
    onUpdateOfferField: (
        offerId: number,
        field: "title" | "description" | "tag" | "url",
        value: string
    ) => void;
    onToggleOfferActive: (offerId: number) => void;
    onDeleteClick: (offerId: number) => void;
    onAddOffer: () => void;
    onResetAllOffers: () => void;
};

export function OffersTable({
    mainColor,
    languages,
    selectedLanguage,
    onLanguageChange,
    displayType,
    onDisplayTypeChange,
    currentOffers,
    uploadedImages,
    onDragEnd,
    onOrderChange,
    onImageClick,
    onUpdateOfferField,
    onToggleOfferActive,
    onDeleteClick,
    onAddOffer,
    onResetAllOffers,
}: OffersTableProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card>
            <SectionHeader>
                <SectionTitle>{t("Offers of the day")}</SectionTitle>
                <ResetButton
                    data-testid="scan-to-offers-reset-all-offers"
                    mainColor={mainColor}
                    onClick={onResetAllOffers}
                    title={t("Reset all")}
                    aria-label={t("Reset all")}
                >
                    <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                </ResetButton>
            </SectionHeader>

            <ImageHeader>
                <Select
                    data-testid="scan-to-offers-display-type-select"
                    value={displayType}
                    onChange={(e) => onDisplayTypeChange(e.target.value)}
                >
                    <option value="desktop">
                        Desktop 1920*550 (png, jpg, jpeg)
                    </option>
                    <option value="mobile">
                        Mobile 750*400 (png, jpg, jpeg)
                    </option>
                </Select>
                <span style={{ fontSize: 12, color: "#999" }}>
                    {displayType === "desktop"
                        ? "1920*550 (png, jpg, jpeg)"
                        : "750*400 (png, jpg, jpeg)"}
                </span>
                <AddButton
                    data-testid="scan-to-offers-add-offer"
                    mainColor={mainColor}
                    onClick={onAddOffer}
                >
                    {t("Add")}
                </AddButton>
            </ImageHeader>

            {/* Language tabs for offers */}
            <LanguageTabs style={{ marginTop: 15 }}>
                {languages.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-offers-table-lang-${lang.code}`}
                        isActive={selectedLanguage === lang.code}
                        mainColor={mainColor}
                        onClick={() => onLanguageChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <TableWrapper>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Table>
                        <thead>
                            <tr>
                                <Th>{t("Order")}</Th>
                                <Th>{t("Image")}</Th>
                                <Th>{t("Title")}</Th>
                                <Th>{t("Description")}</Th>
                                <Th>{t("Tag")}</Th>
                                <Th>URL</Th>
                                <Th>{t("Actions")}</Th>
                            </tr>
                        </thead>
                        <Droppable droppableId="offers-list">
                            {(provided) => (
                                <tbody
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {[...currentOffers]
                                        .sort((a, b) => a.order - b.order)
                                        .map((offer, index) => (
                                            <Draggable
                                                key={offer.id}
                                                draggableId={String(offer.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <tr
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        data-testid={`scan-to-offers-row-${offer.id}`}
                                                        style={{
                                                            ...provided
                                                                .draggableProps
                                                                .style,
                                                            backgroundColor: snapshot.isDragging
                                                                ? "#f0f7ff"
                                                                : "white",
                                                        }}
                                                    >
                                                        <Td>
                                                            <DragHandle
                                                                {...provided.dragHandleProps}
                                                                data-testid={`scan-to-offers-drag-handle-${offer.id}`}
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="currentColor"
                                                                >
                                                                    <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z" />
                                                                </svg>
                                                                <OrderInput
                                                                    data-testid={`scan-to-offers-order-input-${offer.id}`}
                                                                    type="number"
                                                                    value={
                                                                        offer.order
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        onOrderChange(
                                                                            offer.id,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </DragHandle>
                                                        </Td>
                                                        <Td>
                                                            <OfferImageCell
                                                                data-testid={`scan-to-offers-view-image-${offer.id}`}
                                                                hasImage={
                                                                    !!uploadedImages[
                                                                        offer.id
                                                                    ]
                                                                }
                                                                onClick={() =>
                                                                    onImageClick(
                                                                        offer.id
                                                                    )
                                                                }
                                                                title={t(
                                                                    "Image"
                                                                )}
                                                            >
                                                                {uploadedImages[
                                                                    offer.id
                                                                ] ||
                                                                offer.imageUrl ||
                                                                offer.image?.startsWith(
                                                                    "http"
                                                                ) ? (
                                                                    <img
                                                                        src={
                                                                            uploadedImages[
                                                                                offer
                                                                                    .id
                                                                            ] ||
                                                                            offer.imageUrl ||
                                                                            offer.image
                                                                        }
                                                                        alt={
                                                                            offer.image
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <svg
                                                                        width="20"
                                                                        height="20"
                                                                        viewBox="0 0 24 24"
                                                                        fill="#ccc"
                                                                    >
                                                                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                                    </svg>
                                                                )}
                                                            </OfferImageCell>
                                                        </Td>
                                                        <Td>
                                                            <TableInput
                                                                data-testid={`scan-to-offers-title-input-${offer.id}`}
                                                                value={
                                                                    offer.title[
                                                                        selectedLanguage
                                                                    ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                    onUpdateOfferField(
                                                                        offer.id,
                                                                        "title",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder={t(
                                                                    "Offer title"
                                                                )}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <TableInput
                                                                data-testid={`scan-to-offers-description-input-${offer.id}`}
                                                                value={
                                                                    offer
                                                                        .description[
                                                                        selectedLanguage
                                                                    ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                    onUpdateOfferField(
                                                                        offer.id,
                                                                        "description",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder={t(
                                                                    "Offer description"
                                                                )}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <TableInput
                                                                data-testid={`scan-to-offers-tag-input-${offer.id}`}
                                                                value={
                                                                    offer.tag
                                                                }
                                                                onChange={(e) =>
                                                                    onUpdateOfferField(
                                                                        offer.id,
                                                                        "tag",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder={t(
                                                                    "Enter tag"
                                                                )}
                                                                style={{
                                                                    maxWidth: 80,
                                                                }}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <TableInput
                                                                data-testid={`scan-to-offers-url-input-${offer.id}`}
                                                                value={
                                                                    offer.url
                                                                }
                                                                onChange={(e) =>
                                                                    onUpdateOfferField(
                                                                        offer.id,
                                                                        "url",
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder={t(
                                                                    "Enter URL"
                                                                )}
                                                                style={{
                                                                    maxWidth: 120,
                                                                }}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <ActionsCell>
                                                                <SmallToggle
                                                                    data-testid={`scan-to-offers-active-toggle-${offer.id}`}
                                                                    isActive={
                                                                        offer.active
                                                                    }
                                                                    mainColor={
                                                                        mainColor
                                                                    }
                                                                    onClick={() =>
                                                                        onToggleOfferActive(
                                                                            offer.id
                                                                        )
                                                                    }
                                                                    aria-label={t(
                                                                        "Toggle offer visibility"
                                                                    )}
                                                                />
                                                                <DeleteButton
                                                                    data-testid={`scan-to-offers-delete-${offer.id}`}
                                                                    onClick={() =>
                                                                        onDeleteClick(
                                                                            offer.id
                                                                        )
                                                                    }
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                                                    </svg>
                                                                </DeleteButton>
                                                            </ActionsCell>
                                                        </Td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                    {provided.placeholder}
                                </tbody>
                            )}
                        </Droppable>
                    </Table>
                </DragDropContext>
            </TableWrapper>
        </Card>
    );
}
